import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import MaterialSymbol from 'components/material-symbol/material-symbol'

const FILTER_PRIMARY_BLUE = '#0053FF'
const RADIO_ICON_SIZE = 20
const POPOVER_MENU_SHADOW =
  '0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12)'

const useStyles = makeStyles(theme => ({
  filterButton: {
    flexShrink: 0,
    height: 44,
    minHeight: 44,
    padding: theme.spacing(0, 1.5),
    border: '1px solid #E0E0E0',
    borderRadius: 9999,
    color: '#172024',
    backgroundColor: '#FFFFFF',
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1,
    boxShadow: 'none',
    '&:hover': {
      color: '#0053FF',
      borderColor: '#BDBDBD',
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: theme.spacing(0.75),
    },
  },
  filterButtonActive: {
    borderColor: '#0053FF',
    color: '#0053FF',
  },
  filterIcon: {
    fontSize: 20,
  },
  filterPopover: {
    zIndex: 50000,
  },
  filterMenuPaper: {
    borderRadius: 4,
    boxShadow: POPOVER_MENU_SHADOW,
    overflow: 'hidden',
    minWidth: 200,
  },
  filterMenuContent: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  radioIcon: {
    fontSize: RADIO_ICON_SIZE,
    width: RADIO_ICON_SIZE,
    height: RADIO_ICON_SIZE,
    lineHeight: 1,
    overflow: 'hidden',
  },
  filterOption: {
    margin: 0,
    minHeight: 36,
    padding: theme.spacing(0.75, 2),
    width: '100%',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
    },
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#172024',
    },
  },
  filterMenu: {
    width: '100%',
    '& .MuiRadio-root': {
      padding: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
      color: 'rgba(0, 0, 0, 0.38)',
    },
    '& .MuiRadio-colorPrimary.Mui-checked': {
      color: FILTER_PRIMARY_BLUE,
    },
    '& .MuiRadio-colorPrimary:hover': {
      backgroundColor: 'transparent',
    },
  },
  filterMenuFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 1.5, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  applyButton: {
    backgroundColor: FILTER_PRIMARY_BLUE,
    color: '#fff',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#0046d9',
      boxShadow: 'none',
    },
  },
}))

const FilterRadio = props => {
  const classes = useStyles()

  return (
    <Radio
      color="primary"
      icon={
        <MaterialSymbol
          name="radio_button_unchecked"
          className={classes.radioIcon}
          fontSize={RADIO_ICON_SIZE}
          htmlColor="rgba(0, 0, 0, 0.38)"
          aria-hidden
        />
      }
      checkedIcon={
        <MaterialSymbol
          name="radio_button_checked"
          className={classes.radioIcon}
          fill={1}
          fontSize={RADIO_ICON_SIZE}
          htmlColor={FILTER_PRIMARY_BLUE}
          aria-hidden
        />
      }
      {...props}
    />
  )
}

const FilterDropdown = ({
  filterOptions,
  selectedFilter,
  setSelectedFilter,
}) => {
  const classes = useStyles()
  const [pendingSelectedFilter, setPendingSelectedFilter] = useState(
    selectedFilter,
  )

  const [anchorEl, setAnchorEl] = useState(null)
  const filterMenuOpen = Boolean(anchorEl)
  const isFilterActive = selectedFilter && selectedFilter !== 'ALL'

  const handleClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    setPendingSelectedFilter(selectedFilter)
  }, [selectedFilter])

  const toggleFilterSelection = filterOptionKey => {
    setPendingSelectedFilter(filterOptionKey)
  }

  return (
    <Box style={{ position: 'relative', display: 'inline-block' }}>
      <Button
        id="contact-filters"
        aria-controls={filterMenuOpen ? 'contact-filters-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={filterMenuOpen ? 'true' : undefined}
        className={clsx(classes.filterButton, {
          [classes.filterButtonActive]: isFilterActive,
        })}
        disableElevation
        onClick={e => {
          setAnchorEl(e.currentTarget)
        }}
        size="small"
        startIcon={
          <MaterialSymbol
            name="filter_list"
            className={classes.filterIcon}
            aria-hidden
          />
        }
      >
        Filters
      </Button>
      <Popover
        id="contact-filters-menu"
        open={filterMenuOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className={classes.filterPopover}
        marginThreshold={8}
        PaperProps={{
          className: classes.filterMenuPaper,
        }}
      >
        <div className={classes.filterMenuContent}>
          <FormControl
            className={classes.filterMenu}
            component="fieldset"
            fullWidth
          >
            <RadioGroup
              aria-label="filter"
              name="filter"
              value={pendingSelectedFilter}
              onChange={event => toggleFilterSelection(event.target.value)}
            >
              {filterOptions.map(opt => {
                const key = opt.name
                return (
                  <FormControlLabel
                    key={key}
                    className={classes.filterOption}
                    value={key}
                    control={<FilterRadio />}
                    label={opt.description}
                  />
                )
              })}
            </RadioGroup>
          </FormControl>
        </div>
        <div className={classes.filterMenuFooter}>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.applyButton}
            onClick={() => {
              setSelectedFilter(pendingSelectedFilter)
              handleClose()
            }}
          >
            Apply
          </Button>
        </div>
      </Popover>
    </Box>
  )
}

export default FilterDropdown
